import _ from "lodash"
import moment from "moment"

import airlineRestrictions from "../../../../data/airlineRestrictions.json" // eslint-disable-line import/no-restricted-paths
import { BOOKING_STATUSES } from "mmb/consts/booking"
import { BOARDING_PASS_AVAILABILITY_NEVER } from "common/records/Flight"
import {
  hasPaidHandBagsAllowedFromPassengers,
  BAGS_TYPES,
} from "mmb/scenes/AccountBooking/components/Booking/services/bags/tools"
import { SERVICES, SERVICES_STATUSES } from "mmb/consts/additionalServices"

const POBEDA_FREE_BAG_UNTIL = "2017-09-30"

export default class Booking {
  constructor(args) {
    this.id = Number(args.id) // Number
    this.created = args.created // timestamp
    this.authToken = args.authToken // string
    this.status = args.status // string [new/processed/refunded]
    this.blocked = args.blocked // bool
    this.config = args.config // object {config: {allowed_to_change: {}}}
    this.bookingChanges = args.bookingChanges // object {config: {allowed_to_change: {}}}
    this.additionalContacts = args.additionalContacts // List
    this.actions = args.actions // object
    this.additionalBagsPrices = args.additionalBagsPrices // object
    this.insurancePricing = args.insurancePricing // object
    this.flightsHistory = args.flightsHistory // List
    this.additionalServices = args.additionalServices // OrderedSet<Record>
    this.pendingFlightChangeOptions = args.pendingFlightChangeOptions // List
    this.pendingRequests = args.pendingRequests // List
    this.assets = args.assets // object
    this.bagParams = args.bagParams // object
    this.bags = args.bags // number
    this.customer = args.customer // Customer
    this.journey = args.journey // Journey
    this.journeyWithHidden = args.journeyWithHidden // Journey
    this.passengers = args.passengers // List<Passenger>
    this.infantsConditions = args.infantsConditions // object
    this.payment = args.payment // Payment
    this.sandbox = args.sandbox // boolean
    this.ticketing_allowed = args.ticketing_allowed // Null / String
    this.cancelRequested = args.cancelRequested // timestamp
    this.pendingRefund = args.pendingRefund // BookingPendingRefund
    this.seatmaps = args.seatmaps // object
    this.refundOption = args.refundOption // object - grace_period
    this.refundOptions = args.refundOptions // object - type BookingRefundOptions - voluntary
    this.refundMethods = args.refundMethods // Set
    this.netverify = args.netverify // object
    this.scheduleChange = args.scheduleChange // object
    this.billingDetails = args.billingDetails // object
    this.servicePackage = args.servicePackage // object
    this.servicePackage = args.servicePackage // object
    this.paxAta = args.paxAta // object
    this.baggages = args.baggages
    this.meals = args.meals // OrderedMealsInput
    this.checkIn = args.checkIn
    this.availableBaggage = args.availableBaggage
  }

  hasAlternativesOffered(): boolean {
    return (
      this.pendingFlightChangeOptions.size > 0 &&
      this.additionalServices.filter(
        service =>
          service.status === SERVICES_STATUSES.OPEN && service.category === SERVICES.CHANGE_FLIGHTS,
      ).size === 0
    )
  }

  isCompletionNeeded(): boolean {
    return (
      this.status === BOOKING_STATUSES.CONFIRMED &&
      this.config.document.need !== BOARDING_PASS_AVAILABILITY_NEVER &&
      this.getIncompletePassengers().length > 0
    )
  }

  isCompletionNeededByDefault(): boolean {
    return (
      this.status === BOOKING_STATUSES.CONFIRMED &&
      this.config.document.need !== BOARDING_PASS_AVAILABILITY_NEVER
    )
  }

  requiresBulkCompletion() {
    return this.journey
      .getAirlines()
      .some(airline => _.get(airlineRestrictions, [airline, "bulkCheckin"]))
  }

  canChange(what): boolean {
    return (
      !this.blocked &&
      this.status === "confirmed" &&
      this.journey.getTimeUntil("hours") >= this.config.allowedToChange[what]
    )
  }

  canOrderAdditionalServicesBags(): boolean {
    const hoursUntilLastDeparture = this.journey
      .get("trips")
      .last()
      .getDeparture()
      .getIn(["when", "utc"])
      .diff(moment.utc(), "hours")
    return hoursUntilLastDeparture >= this.config.allowedToChange.bags
  }

  canChangeDocuments(): boolean {
    const hoursAllowedToChange = this.config.allowedToChange.documents
    const hoursUntilLastDeparture = this.journey
      .getFlights()
      .last()
      .getIn(["departure", "when", "utc"])
      .diff(moment.utc(), "hours")
    return hoursUntilLastDeparture >= hoursAllowedToChange
  }

  hasChangeFlightsBlocked() {
    const hasConfirmedChangeFlight = this.additionalServices.filter(
      x => x.category === SERVICES.CHANGE_FLIGHTS && x.status === SERVICES_STATUSES.CONFIRMED,
    ).size

    return _.some([
      this.blocked,
      hasConfirmedChangeFlight, // already have confirmed change flight
      this.additionalServices.filter(
        (
          x, // already have "blocking" additional service
        ) =>
          x.status === SERVICES_STATUSES.PENDING ||
          x.status === SERVICES_STATUSES.CLOSED ||
          x.status === SERVICES_STATUSES.OFFERED ||
          x.status === SERVICES_STATUSES.OPEN,
      ).size,
      this.status !== BOOKING_STATUSES.CONFIRMED,
    ])
  }

  // additional services that don't require confirmation
  hasAdditionalServicesAllowed(): boolean {
    return (
      this.journey.endsInFuture() &&
      (this.status === BOOKING_STATUSES.CONFIRMED || this.status === BOOKING_STATUSES.CLOSED)
    )
  }

  // additional services that require confirmation of booking
  hasAllAdditionalServicesAllowed(): boolean {
    return this.journey.endsInFuture() && this.status === BOOKING_STATUSES.CONFIRMED
  }

  hasPendingServicesAllowed(): boolean {
    return this.additionalServices.filter(x => x.status === SERVICES_STATUSES.OFFERED).size > 0
  }

  hasNetverifyAllowed(): boolean {
    return (
      this.netverify && (this.netverify.status === "initiate" || this.netverify.status === "retry")
    )
  }

  hasPaidHandBagsAllowed(): boolean {
    return !!hasPaidHandBagsAllowedFromPassengers(this.passengers, this.journey)
  }

  hasBagsWithPaxAssociated(): boolean {
    return _.some(this.passengers.map(pax => _.isNumber(pax.bags)))
  }

  hasChangeFlightAllowed(): boolean {
    return this.journey.isChangeAllowedForAnyTrip(this.config.allowedToChange.flights)
  }

  // POBEDA doesn't support free baggage anymore
  // ===

  hasFreePobedaBagCondition(): boolean {
    const milestoneMoment = moment.utc(POBEDA_FREE_BAG_UNTIL, "YYYY-MM-DD").startOf("day")
    return this.journey.hasFreePobedaBag() && moment.utc(this.created).isBefore(milestoneMoment)
  }

  canChangeFlights(): boolean {
    return (
      !this.hasChangeFlightsBlocked() &&
      this.hasChangeFlightAllowed() &&
      !this.journey.hasTransfer()
    )
  }

  canChangeBagsUntilMoment(): string {
    const departureMoment = this.journey.getDeparture().getIn(["when", "utc"])
    return moment(departureMoment).subtract(this.config.allowedToChange.bags, "hours")
  }

  canChangeBags(): boolean {
    // TODO rewrite to selector
    const canChangeUntilMoment = this.canChangeBagsUntilMoment()

    return (
      !this.blocked &&
      this.status === BOOKING_STATUSES.CONFIRMED &&
      moment().isBefore(canChangeUntilMoment)
    )
  }

  // eslint-disable-next-line flowtype/no-weak-types
  getCommonBagsProps(): Object {
    return {
      bagParams: this.bagParams,
      bagChanges: this.additionalServices.filter(
        x =>
          x.category === SERVICES.BAGGAGE &&
          // Both pending and closed means "processing" in bags scenario
          (x.status === SERVICES_STATUSES.PENDING || x.status === SERVICES_STATUSES.CLOSED),
      ),
      extraHandBagsCount: this.getExtraHandBagsCount(),
      hasFreePobedaBag: this.hasFreePobedaBagCondition(),
      handBagsCount: this.getHandBagsCount(),
      hasPaidHandBagsAllowed: this.hasPaidHandBagsAllowed(),
      holdBagsCount: this.getBagsCount(),
      passengers: this.passengers,
      passengersCount: this.passengers.length,
      preventPriceElement: true,
      trolley: this.infantsConditions.trolley,
    }
  }

  // total holdBags count
  getBagsCount(): number {
    // Filter just additionalServices that with action == "addBags"
    const additionalServices = this.additionalServices.filter(x => x.category === SERVICES.BAGGAGE)

    const completedAdditionalServices = additionalServices.filter(
      x => x.status === SERVICES_STATUSES.CONFIRMED,
    )

    const amountOfBagsOfCompletedAdditionalServices = completedAdditionalServices.reduce(
      (sum, x) => sum + (x.params?.bags || 0), // there's a case with missing params.bags
      0,
    )

    return this.hasBagsWithPaxAssociated()
      ? this.passengers.reduce((acc, pax) => (pax.bags || 0) + acc, 0) +
          amountOfBagsOfCompletedAdditionalServices
      : this.bags + amountOfBagsOfCompletedAdditionalServices
  }

  getHandBagsCount(): number {
    const _handBagsAllowedForInfants =
      this.infantsConditions && this.infantsConditions.get("differentHandWeight")
        ? this.infantsConditions.get("handWeight") > 0
        : _.isNumber(this.bagParams.get(BAGS_TYPES.HAND).get("length"))

    const prioFlightIds = this.journey.getPrioCabinBagsFlights().map(flight => flight.get("id"))

    // If some flights have cabin baggage only with priority boarding,
    // the passengers needs to have ordered prio boarding for all these flights
    const prefilteredPassengers =
      prioFlightIds.length > 0
        ? this.passengers.filter(passenger =>
            _.every(prioFlightIds, prioFlightId =>
              _.get(passenger, ["priorityBoarding", prioFlightId, "is_ordered"], false),
            ),
          )
        : this.passengers

    return this.hasPaidHandBagsAllowed()
      ? prefilteredPassengers.reduce((acc, pax) => (pax.handBags || 0) + acc, 0) // pax.handBags || 0 ugly condition force be to show 0 instead of nothing
      : prefilteredPassengers.filter(pax => pax.hasBagsAllowed(_handBagsAllowedForInfants)).length
  }

  getExtraHandBagsCount(): number {
    return this.passengers.reduce((acc, pax) => acc + pax.extraHandBags, 0)
  }

  getIncompletePassengers(): Array {
    return this.passengers.filter(pax => !pax.document.code)
  }

  getAdditionalServicesToPay() {
    return this.additionalServices.filter(x => x.status === SERVICES_STATUSES.OPEN).toJS()
  }

  getPendingInsuranceAdditionalServicesByPassenger(pid): ?string {
    let result

    const additionalServices = this.additionalServices.filter(
      x => x.category === SERVICES.INSURANCE && x.status === SERVICES_STATUSES.OPEN,
    )

    additionalServices.forEach(x => {
      x.params.forEach(p => {
        if (p.pid === pid) {
          result = p.insurance
        }
      })
    })

    return result
  }
}
